import { Link } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import SEO from "@/components/SEO";

const COUPLE = "/images/about-residential.jpg";

const PILLARS = [
  "Specialist park home knowledge",
  "Insurance-related damage experience",
  "Practical repair and scope understanding",
  "Independent support before committing to cost",
  "Focused on preventing expensive mistakes",
];

export default function About() {
  return (
    <div data-testid="about-page">
      <SEO
        title="About Park Home Support | Specialist Park Home Guidance"
        description="Specialist support built on real park home and insurance claims experience. Independent, non-regulated technical guidance for UK residential park home owners and operators."
        canonical="https://parkhomesupport.co.uk/about"
      />
      <section className="hero-gradient section-pad">
        <div className="container-phs">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <div>
              <span className="eyebrow">About Us</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] text-[#071A33]">
                Specialist Support Built on Real Park Home and <span className="text-[#064E2E]">Insurance Claims Experience.</span>
              </h1>
              <p className="mt-6 text-lg text-[#334155]">
                Park Home Support was created to give park home owners and operators access to practical, specialist guidance before costly decisions are made. Our experience spans park home repairs, site inspections, contractor scopes, damage assessment and insurance-related claim issues.
              </p>
              <p className="mt-4 text-[#334155]">
                We understand the difference between a general property problem and a park home-specific issue. That knowledge matters when deciding whether work is needed, whether costs are reasonable, and whether insurance may be relevant.
              </p>
              <Link to="/contact" className="btn-primary mt-8" data-testid="about-contact-btn">Contact us</Link>
            </div>
            <div>
              <div className="img-plate">
                <img src={COUPLE} alt="Park home owners" className="w-full h-[480px] object-cover"/>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-phs max-w-4xl">
          <span className="eyebrow">What sets us apart</span>
          <h2 className="font-display text-3xl sm:text-4xl mt-3 text-[#071A33] leading-tight">Five things that matter.</h2>
          <ul className="mt-10 space-y-4">
            {PILLARS.map((p) => (
              <li key={p} className="card-default flex gap-3 items-start" data-testid={`about-pillar-${p.toLowerCase().replace(/[^a-z]+/g,"-")}`}>
                <CheckCircle2 className="text-[#0F766E] mt-1 shrink-0" size={22}/>
                <span className="text-[#071A33] text-lg font-medium">{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
      <section className="section-pad bg-[#F7F4ED]" data-testid="about-real-experience">
        <div className="container-phs">
          <div className="rounded-3xl bg-white p-8 md:p-12 border border-[#D7E7DF] shadow-sm max-w-4xl mx-auto">
            <span className="eyebrow">Built on Real Experience</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3 leading-tight text-[#071A33]">
              Built on hands-on park home experience.
            </h2>
            <p className="mt-5 text-[#475569] text-lg leading-relaxed">
              Park Home Support is built on hands-on experience dealing with park home repairs, inspections, contractor scopes and insurance-related damage. This isn&apos;t generic advice — it&apos;s based on understanding how these issues actually play out in the real world.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
